import * as React from 'react';
import './slots-page.component.css';
import Page from "../../../components/core/Page";
import {IonButton, IonList, IonText} from "@ionic/react";

import {Booking, Company, Employee, Facility, Profile, LessonType, ProfilePackage, Slot} from "../../../models";
import {formatFreezeDate} from "../../../utils/frozen";
import SlotRow from "../../../components/slots/SlotRow/SlotRow";
import SlotRowSkeleton from "../../../components/slots/SlotRow/SlotRowSkeleton";
import SlotModal from "../../../components/slots/SlotModal/SlotModal";
import SlotFilterModal from "../../../components/slots/SlotFilterModal/SlotFilterModal";
import {DatesToolbar} from "./DatesToolbar";
import {FiltersToolbar} from "./FiltersToolbar";

export interface AvailableDateOption {
    active: boolean,
    ts: number,
    date: Date,
    isBooked: boolean,
    isWaitingBooked: boolean,
    isPast: boolean,
    isHoliday: boolean,
    isFrozen: boolean
}

type SlotsPageProps = {
    company?: Company;
    learner?: Profile;
    profilePackage?: ProfilePackage;
    frozenUntil: string | null;
    isSelectedDateFrozen: boolean;
    selectedHolidayLabel: string | null;
    availableDates: AvailableDateOption[];
    bookings?: Booking[];
    slots?: Slot[];
    slotTypes?: LessonType[];
    facilities?: Facility[];
    employees?: Employee[];

    // Loading states
    isInitialLoading: boolean;
    isRefreshing: boolean;
    isChangingBooking: boolean;
    isChangingWeek: boolean;
    actionsDisabled: boolean;

    // Booking Events
    onBookClicked: (slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onWaitClicked: (slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onCancelWaitClicked: (waitingBookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onDeleteClicked: (bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onCancelClicked: (bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onUncancelClicked: (bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;

    // Move Events (recurring self-service: relocate a booked occurrence to another slot)
    onMoveClicked: (bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onCancelMoveClicked: () => void;
    movingBooking: { bookingId: number; description: string } | null;

    // Dates
    setSelectedDate: (date: number) => void;
    selectedDate: number;
    selectedDateIsToday: boolean;
    selectedWeekIndex: number;
    weeksAvailableFuture: number;
    weeksAvailablePast: number;

    // Filtering
    isSlotFilterModalOpen: boolean;
    onSlotFilterOpenClick: () => void;

    onCloseSlotFilterModal: () => void;
    filterAvailableOnly: boolean;
    setFilterAvailableOnly: (availableOnly: boolean) => void;
    filterBookedOnly: boolean;
    setFilterBookedOnly: (bookedOnly: boolean) => void;
    filterSlotType?: number;
    setFilterSlotType: (id?: number) => void;
    filterFacility?: number;
    setFilterFacility: (id?: number) => void;
    filterEmployee?: number;
    setFilterEmployee: (id?: number) => void;

    onPreviousWeekClicked: () => void;
    onNextWeekClicked: () => void;

    // Slot UI
    selectedSlot?: Slot;
    isSlotModalOpen: boolean;
    onSlotClick: (slot: Slot) => void;
    onCloseModal: () => void;
};

export const SlotsPage: React.FC<SlotsPageProps> = props => {
    const {
        company,
        learner,
        profilePackage,
        frozenUntil,
        isSelectedDateFrozen,
        selectedHolidayLabel,
        availableDates,
        selectedWeekIndex,
        slots,
        facilities,
        employees,
        slotTypes,
        isInitialLoading,
        isRefreshing,
        isChangingBooking,
        isChangingWeek,
        actionsDisabled,

        onBookClicked,
        onWaitClicked,
        onCancelWaitClicked,
        onDeleteClicked,
        onCancelClicked,
        onUncancelClicked,
        onMoveClicked,
        onCancelMoveClicked,
        movingBooking,
        setSelectedDate,
        selectedDate,
        selectedDateIsToday,
        onPreviousWeekClicked,
        onNextWeekClicked,
        weeksAvailableFuture,
        weeksAvailablePast,

        filterAvailableOnly,
        setFilterAvailableOnly,
        filterBookedOnly,
        setFilterBookedOnly,
        filterSlotType,
        setFilterSlotType,
        filterFacility,
        setFilterFacility,
        filterEmployee,
        setFilterEmployee,

        isSlotFilterModalOpen,
        onCloseSlotFilterModal,
        onSlotFilterOpenClick,

        selectedSlot,
        isSlotModalOpen,
        onSlotClick,
        onCloseModal,
    } = props;

    const isSelectedDateHoliday = selectedHolidayLabel !== null;
    const isFiltered = !!(filterAvailableOnly || filterBookedOnly || filterFacility || filterEmployee || filterSlotType);
    // In move mode every eligible slot's "Book" affordance becomes the move
    // destination picker — same button, different label and handler downstream.
    const moveMode = !!movingBooking;
    const bookText = moveMode ? 'Move Here' : undefined;

    return (
        <Page className={"slots-page ion-text-center"}
              title={`${profilePackage?.name || 'Package Loading...'}`}
              subtitle={learner?.fullName}
              showMenuButton={false}
              showBackButton={true}
              backRouterLink="/home"
              showLoading={!profilePackage}
        >

            <DatesToolbar 
                availableDates={availableDates}
                selectedWeekIndex={selectedWeekIndex}
                weeksAvailablePast={weeksAvailablePast}
                weeksAvailableFuture={weeksAvailableFuture}
                isRefreshing={isRefreshing}
                isChangingBooking={isChangingBooking}
                isChangingWeek={isChangingWeek}
                onPreviousWeekClicked={onPreviousWeekClicked}
                onNextWeekClicked={onNextWeekClicked}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
            />

            {frozenUntil && (
                <IonText color="medium" className="frozen-profile-notice">
                    <p>This profile is frozen until {formatFreezeDate(frozenUntil)}.
                        Sessions during the freeze can't be attended or booked.</p>
                </IonText>
            )}

            {movingBooking && (
                <IonText color="primary" className="moving-booking-notice">
                    <p>
                        Pick a new slot for {movingBooking.description}.{' '}
                        <IonButton fill="clear" size="small" onClick={onCancelMoveClicked}>Cancel Move</IonButton>
                    </p>
                </IonText>
            )}

            {(profilePackage?.canManageBookings && <FiltersToolbar
                filterBookedOnly={filterBookedOnly}
                facilities={facilities}
                filterFacility={filterFacility}
                employees={employees}
                filterEmployee={filterEmployee}
                slotTypes={slotTypes}
                filterSlotType={filterSlotType}
                filterAvailableOnly={filterAvailableOnly}
                setFilterAvailableOnly={setFilterAvailableOnly}
                onSlotFilterOpenClick={onSlotFilterOpenClick}
                isFiltered={isFiltered}
                setFilterFacility={setFilterFacility}
                setFilterSlotType={setFilterSlotType}
                setFilterEmployee={setFilterEmployee}
                setFilterBookedOnly={setFilterBookedOnly}
                isRefreshing={isRefreshing}
                selectedDate={selectedDate}
            />)}
            {
                // A frozen date must never render bookable-looking slot rows —
                // show why the day is empty instead of the list.
                isSelectedDateFrozen && !isInitialLoading && !isChangingWeek &&
                    <IonText>
                        <h4>Frozen — sessions on this date can't be attended.</h4>
                    </IonText>
            }
            {
                isSelectedDateHoliday && !isInitialLoading && !isChangingWeek &&
                    <IonText>
                        <h4>{selectedHolidayLabel}. No sessions on this day.</h4>
                    </IonText>
            }
            {
                !isSelectedDateFrozen && !isSelectedDateHoliday && !isInitialLoading && !isRefreshing && ((!slots || slots.length === 0) &&
                    <IonText>
                        <>
                            <h4>{(((profilePackage?.isRecurring && `No bookings to ${selectedDateIsToday ? 'today' : 'show'}`))
                                || (
                                    filterBookedOnly ? (<> No bookings found. <br key="br-no-slot-1"/><br key="br-no-slot-2"/> Change the [My Bookings] filter to see what's available</>)
                                        : "No slots available"
                                ))}</h4>
                        </>
                    </IonText>
                )
            }
            {
                    <IonList>
                        {(isInitialLoading || isChangingWeek) && <SlotRowSkeleton/>}

                        {(!isInitialLoading && !isChangingWeek) && !isSelectedDateFrozen && !isSelectedDateHoliday && profilePackage && company && slots && slots.length > 0
                            && slots.map((slot) => (
                                <SlotRow
                                    key={`slot-${slot.id}`}
                                    company={company}
                                    slot={slot}
                                    profilePackage={profilePackage}
                                    onRowClicked={onSlotClick}
                                    onCancelClicked={onCancelClicked}
                                    onUncancelClicked={onUncancelClicked}
                                    onDeleteClicked={onDeleteClicked}
                                    onBookClicked={onBookClicked}
                                    onWaitClicked={onWaitClicked}
                                    onCancelWaitClicked={onCancelWaitClicked}
                                    onMoveClicked={onMoveClicked}
                                    bookText={bookText}
                                    moveMode={moveMode}
                                    actionsDisabled={actionsDisabled}
                                />
                            ))
                        }
                    </IonList>
            }
            {profilePackage && (
                <SlotFilterModal
                    isOpen={isSlotFilterModalOpen}
                    onClose={onCloseSlotFilterModal}

                    company={company}
                    facilities={facilities}
                    employees={employees}
                    slotTypes={slotTypes}

                    filterAvailableOnly={filterAvailableOnly}
                    setFilterAvailableOnly={setFilterAvailableOnly}
                    filterBookedOnly={filterBookedOnly}
                    setFilterBookedOnly={setFilterBookedOnly}
                    filterSlotType={filterSlotType}
                    setFilterSlotType={setFilterSlotType}
                    filterFacility={filterFacility}
                    setFilterFacility={setFilterFacility}
                    filterEmployee={filterEmployee}
                    setFilterEmployee={setFilterEmployee}

                />
            )}
            {profilePackage && company && selectedSlot && (
                <SlotModal
                    isOpen={isSlotModalOpen}
                    onClose={onCloseModal}
                    company={company}
                    slot={selectedSlot}
                    profilePackage={profilePackage}
                    actionsDisabled={actionsDisabled}
                    onCancelClicked={onCancelClicked}
                    onUncancelClicked={onUncancelClicked}
                    onDeleteClicked={onDeleteClicked}
                    onBookClicked={onBookClicked}
                    onWaitClicked={onWaitClicked}
                    onCancelWaitClicked={onCancelWaitClicked}
                    onMoveClicked={onMoveClicked}
                    bookText={bookText}
                    moveMode={moveMode}
                />
            )}
        </Page>
    );
};
